import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Home, CreditCard, MessageCircle, LogOut, Send, Clock, CheckCircle, AlertCircle, HelpCircle, Edit, Users, Baby, TreePine, Tent, Gift } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { guestLogin, guestVerify, getGuestMe, getGuestQuestions, createGuestQuestion } from '@/lib/api';

export const LoginForm = ({ onLogin }) => {
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await guestLogin(email);
      toast.success('Code de vérification envoyé !');
      setStep('code');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!code) return;
    
    setLoading(true);
    try {
      const res = await guestVerify(email, code);
      if (res.data.is_admin) {
        localStorage.setItem('admin_token', res.data.token);
        localStorage.removeItem('guest_token');
      } else {
        localStorage.setItem('guest_token', res.data.token);
      }
      toast.success('Connexion réussie !');
      onLogin();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <Card className="card-elegant">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-full bg-terracotta/10 flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-terracotta" />
          </div>
          <CardTitle className="font-display text-2xl text-charcoal">
            {step === 'email' ? 'Connexion' : 'Vérification'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <p className="text-stone text-center text-sm mb-4">
                Entrez l'email utilisé lors de votre confirmation de présence pour accéder à votre espace.
              </p>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Adresse email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" />
                  <Input
                    data-testid="guest-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              <Button
                data-testid="guest-email-submit"
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
              >
                {loading ? 'Envoi...' : 'Recevoir mon code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <p className="text-stone text-center text-sm mb-4">
                Un code à 6 chiffres a été envoyé à <strong>{email}</strong>
              </p>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Code de vérification</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" />
                  <Input
                    data-testid="guest-code-input"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="pl-10 text-center text-2xl tracking-widest"
                    maxLength={6}
                    required
                  />
                </div>
              </div>
              <Button
                data-testid="guest-code-submit"
                type="submit"
                className="btn-primary w-full"
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Vérification...' : 'Se connecter'}
              </Button>
              <button
                type="button"
                onClick={() => setStep('email')}
                className="w-full text-center text-sm text-terracotta hover:underline"
              >
                Modifier l'email
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const GuestDashboard = ({ onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [guestData, setGuestData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [meRes, questionsRes] = await Promise.all([
        getGuestMe(),
        getGuestQuestions()
      ]);
      setGuestData(meRes.data);
      setQuestions(questionsRes.data);
    } catch (error) {
      console.error('Error loading guest data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('guest_token');
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    if (!newQuestion.trim()) return;
    
    setSubmitting(true);
    try {
      await createGuestQuestion(guestData.rsvp.id, newQuestion);
      toast.success('Question envoyée !');
      setNewQuestion('');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('guest_token');
    onLogout();
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-pulse text-stone">Chargement...</div>
      </div>
    );
  }

  const { rsvp, accommodation, payment } = guestData || {};

  const getAccommodationStatus = () => {
    if (!accommodation) return null;
    if (accommodation.accommodation_type === 'none') return null;
    
    const statusMap = {
      pending: { label: 'En attente de confirmation', icon: Clock, color: 'bg-amber-100 text-amber-800' },
      confirmed: { label: 'Confirmé', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
      waitlist: { label: 'Liste d\'attente', icon: AlertCircle, color: 'bg-orange-100 text-orange-800' }
    };
    
    return statusMap[accommodation.status] || statusMap.pending;
  };

  const accStatus = getAccommodationStatus();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl md:text-3xl text-charcoal">
            Bienvenue, {rsvp?.family_name}
          </h2>
          <p className="text-stone">{rsvp?.email}</p>
        </div>
        <Button
          data-testid="guest-logout-btn"
          variant="outline"
          onClick={handleLogout}
          className="gap-2"
        >
          <LogOut className="w-4 h-4" />
          Déconnexion
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Confirmation Status */}
        <Card className="card-elegant">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center">
                <User className="w-6 h-6 text-terracotta" />
              </div>
              <div>
                <CardTitle className="text-lg">Votre réponse</CardTitle>
              </div>
            </div>
            <Link to={`/rsvp?edit=${rsvp?.id}`}>
              <Button variant="outline" size="sm" className="gap-2">
                <Edit className="w-4 h-4" />
                Modifier
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-stone">Présence :</span>
                <Badge className={rsvp?.attending ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {rsvp?.attending ? 'Confirmée' : 'Absent'}
                </Badge>
              </div>
              {rsvp?.attending && (
                <>
                  <div className="border-t pt-3">
                    <p className="text-sm font-medium text-charcoal mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-terracotta" />
                      Adultes ({rsvp?.adults?.length || 0})
                    </p>
                    <ul className="space-y-1 pl-6">
                      {rsvp?.adults?.map((adult, idx) => (
                        <li key={idx} className="text-sm text-stone">
                          {adult.civility} {adult.first_name} {adult.last_name}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {rsvp?.children?.length > 0 && (
                    <div className="border-t pt-3">
                      <p className="text-sm font-medium text-charcoal mb-2 flex items-center gap-2">
                        <Baby className="w-4 h-4 text-terracotta" />
                        Enfants ({rsvp.children.length})
                      </p>
                      <ul className="space-y-1 pl-6">
                        {rsvp.children.map((child, idx) => (
                          <li key={idx} className="text-sm text-stone">
                            {child.first_name} ({child.age} ans)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {rsvp?.dietary_restrictions && (
                    <div className="border-t pt-3">
                      <p className="text-sm font-medium text-charcoal mb-1">Restrictions alimentaires</p>
                      <p className="text-sm text-stone">{rsvp.dietary_restrictions}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Accommodation Status */}
        <Card className="card-elegant">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center">
                <Home className="w-6 h-6 text-terracotta" />
              </div>
              <div>
                <CardTitle className="text-lg">Hébergement</CardTitle>
              </div>
            </div>
            {rsvp?.attending && (
              <Link to={`/hebergement/${rsvp?.id}`}>
                <Button variant="outline" size="sm" className="gap-2">
                  <Edit className="w-4 h-4" />
                  {accommodation ? 'Modifier' : 'Demander'}
                </Button>
              </Link>
            )}
          </CardHeader>
          <CardContent>
            {!accommodation || accommodation.accommodation_type === 'none' ? (
              <div className="text-center py-4">
                <p className="text-stone mb-3">Aucune demande d'hébergement</p>
                {rsvp?.attending && (
                  <Link to={`/hebergement/${rsvp?.id}`}>
                    <Button variant="outline" className="gap-2">
                      <Home className="w-4 h-4" />
                      Faire une demande
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-stone">Type :</span>
                  <span className="font-medium">
                    {accommodation.accommodation_type === 'domaine' ? 'Domaine' : 'Hôtel'}
                  </span>
                </div>
                
                {/* Préférence de logement */}
                {accommodation.preferred_lodging && (
                  <div className="flex items-center justify-between">
                    <span className="text-stone">Préférence :</span>
                    <span className="font-medium flex items-center gap-2">
                      {accommodation.preferred_lodging === 'chambre' && <><Home className="w-4 h-4 text-blue-500" /> Chambre</>}
                      {accommodation.preferred_lodging === 'cabane' && <><TreePine className="w-4 h-4 text-green-500" /> Cabane</>}
                      {accommodation.preferred_lodging === 'tente' && <><Tent className="w-4 h-4 text-amber-500" /> Tente cloche</>}
                      {accommodation.preferred_lodging === 'tente_perso' && <><Tent className="w-4 h-4 text-emerald-500" /> Ma tente</>}
                    </span>
                  </div>
                )}
                
                {/* Logement attribué */}
                {accommodation.assigned_lodging && (
                  <div className="flex items-center justify-between">
                    <span className="text-stone">Logement attribué :</span>
                    <span className="font-medium text-green-600 flex items-center gap-2">
                      {accommodation.assigned_lodging === 'chambre' && <><Home className="w-4 h-4" /> Chambre</>}
                      {accommodation.assigned_lodging === 'cabane' && <><TreePine className="w-4 h-4" /> Cabane</>}
                      {accommodation.assigned_lodging === 'tente' && <><Tent className="w-4 h-4" /> Tente cloche</>}
                      <CheckCircle className="w-4 h-4" />
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-stone">Adultes :</span>
                  <span className="font-medium">{accommodation.num_adults || rsvp?.adults?.length || 0}</span>
                </div>
                {(accommodation.num_children > 0 || rsvp?.children?.length > 0) && (
                  <div className="flex items-center justify-between">
                    <span className="text-stone">Enfants :</span>
                    <span className="font-medium text-green-600">{accommodation.num_children || rsvp?.children?.length} (gratuit)</span>
                  </div>
                )}
                
                {accStatus && (
                  <div className="flex items-center justify-between">
                    <span className="text-stone">Statut :</span>
                    <Badge className={accStatus.color}>
                      <accStatus.icon className="w-3 h-3 mr-1" />
                      {accStatus.label}
                    </Badge>
                  </div>
                )}
                
                {/* Montant */}
                {accommodation.accommodation_type === 'domaine' && accommodation.assigned_lodging && (
                  <div className="flex items-center justify-between border-t pt-3 mt-3">
                    <span className="text-stone">Montant :</span>
                    {accommodation.should_pay === false ? (
                      <span className="font-bold text-emerald-600 flex items-center gap-2">
                        <Gift className="w-4 h-4" />
                        Offert par les mariés
                      </span>
                    ) : (
                      <span className="font-bold text-terracotta">{accommodation.calculated_amount || 0}€</span>
                    )}
                  </div>
                )}
                
                {accommodation.accommodation_type === 'domaine' && accommodation.status === 'confirmed' && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800">
                      <CheckCircle className="w-4 h-4 inline mr-2" />
                      Votre hébergement au domaine est confirmé. 
                      {accommodation.should_pay === false ? (
                        <span> Les frais sont pris en charge par les mariés.</span>
                      ) : !payment || payment.status !== 'paid' ? (
                        <span> Pensez à effectuer le paiement avant le 30 juin 2026.</span>
                      ) : (
                        <span> Votre paiement a été reçu.</span>
                      )}
                    </p>
                  </div>
                )}
                {accommodation.status === 'pending' && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <Clock className="w-4 h-4 inline mr-2" />
                      Votre demande est en cours de traitement. Vous recevrez un email dès confirmation.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment Status (if applicable) */}
      {accommodation && accommodation.accommodation_type === 'domaine' && accommodation.status === 'confirmed' && (
        <Card className="card-elegant">
          <CardHeader className="flex flex-row items-center gap-4 pb-2">
            <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-terracotta" />
            </div>
            <div>
              <CardTitle className="text-lg">Paiement hébergement</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-stone">Montant :</span>
                <span className="font-medium text-lg">{accommodation.num_people * 65}€</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone">Statut :</span>
                <Badge className={payment?.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                  {payment?.status === 'paid' ? 'Payé' : 'En attente'}
                </Badge>
              </div>
              {(!payment || payment.status !== 'paid') && (
                <Link to={`/paiement/${rsvp.id}`}>
                  <Button className="btn-primary w-full mt-4">
                    Procéder au paiement
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Questions Section */}
      <Card className="card-elegant">
        <CardHeader className="flex flex-row items-center gap-4 pb-2">
          <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-terracotta" />
          </div>
          <div>
            <CardTitle className="text-lg">Vos questions</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {/* New Question Form */}
          <form onSubmit={handleSubmitQuestion} className="mb-6">
            <Textarea
              data-testid="guest-question-input"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="Posez votre question aux mariés..."
              className="mb-3"
              rows={3}
            />
            <Button
              data-testid="guest-question-submit"
              type="submit"
              className="btn-primary gap-2"
              disabled={submitting || !newQuestion.trim()}
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Envoi...' : 'Envoyer'}
            </Button>
          </form>

          {/* Questions List */}
          {questions.length === 0 ? (
            <p className="text-stone text-center py-4">Aucune question posée</p>
          ) : (
            <div className="space-y-4">
              {questions.map((q) => (
                <div key={q.id} className="border rounded-lg p-4 bg-bone/50">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-terracotta mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-charcoal font-medium">{q.question}</p>
                      <p className="text-xs text-stone mt-1">
                        Posée le {new Date(q.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  {q.answer ? (
                    <div className="mt-3 pl-8 border-l-2 border-terracotta">
                      <p className="text-stone">{q.answer}</p>
                      <p className="text-xs text-stone mt-1">
                        Répondu le {new Date(q.answered_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 pl-8">
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        <Clock className="w-3 h-3 mr-1" />
                        En attente de réponse
                      </Badge>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* FAQ Link */}
      <div className="text-center py-4">
        <Link to="/faq" className="text-terracotta hover:underline inline-flex items-center gap-2">
          <HelpCircle className="w-4 h-4" />
          Consultez notre FAQ
        </Link>
      </div>
    </div>
  );
};

const GuestSpacePage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if guest is already authenticated
    const token = localStorage.getItem('guest_token');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative py-16 md:py-24 bg-terracotta-light/20 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <User className="w-12 h-12 text-terracotta mx-auto mb-6" />
            <h1 className="font-display text-4xl md:text-5xl text-charcoal mb-4" data-testid="guest-space-title">
              Espace invité
            </h1>
            <p className="text-stone text-lg">
              {isAuthenticated 
                ? 'Suivez le statut de votre confirmation et de votre hébergement'
                : 'Connectez-vous pour accéder à votre espace personnel'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="px-6">
          {isAuthenticated ? (
            <GuestDashboard onLogout={() => setIsAuthenticated(false)} />
          ) : (
            <LoginForm onLogin={() => setIsAuthenticated(true)} />
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default GuestSpacePage;
